import { useQueryClient } from '@tanstack/react-query'
import { Building2, Cpu, ExternalLink, Gift, Inbox, LayoutDashboard, LogOut, ScrollText, Settings2, ShieldCheck, Users } from 'lucide-react'
import { Navigate, NavLink, Outlet, useNavigate } from 'react-router-dom'
import { Wordmark } from '../../components/layout/Logo'
import { Spinner } from '../../components/ui/misc'
import { ADMIN_ROLE_LABEL, adminAuthClient, useAdminSession } from '../session'

const NAV = [
  { to: '/', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/waitlist', label: 'Beta access', icon: Inbox },
  { to: '/orgs', label: 'Organizations', icon: Building2 },
  { to: '/users', label: 'Users', icon: Users },
  { to: '/credits', label: 'Credits & codes', icon: Gift },
  { to: '/settings', label: 'Sign-up settings', icon: Settings2 },
  { to: '/gpu', label: 'GPU', icon: Cpu },
  { to: '/team', label: 'Team', icon: ShieldCheck },
  { to: '/audit', label: 'Audit log', icon: ScrollText },
]

/** The admin app's frame. The API checks the admin session (ADMIN_EMAILS only) on every call; this only routes. */
export function AdminLayout() {
  const { data: session, isLoading } = useAdminSession()
  const qc = useQueryClient()
  const navigate = useNavigate()
  if (isLoading) return <Spinner />
  if (!session?.user) return <Navigate to="/login" replace />
  const signOut = async () => {
    await adminAuthClient.signOut()
    qc.clear()
    navigate('/login')
  }
  return (
    <div className="admin-app flex h-screen bg-surface-sunken">
      <aside className="hidden w-52 shrink-0 flex-col md:flex">
        <div className="px-4 pb-4 pt-5">
          <Wordmark product="Admin" />
        </div>
        <nav className="mt-2 flex-1 space-y-px">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `mx-2 flex items-center gap-2.5 rounded-lg px-2.5 py-[7px] text-[13.5px] ${isActive ? 'bg-surface-raised text-ink shadow-token-sm ring-1 ring-[color:var(--border-subtle)]' : 'text-ink-secondary hover:bg-surface-hover hover:text-ink'}`
              }
            >
              <Icon size={17} strokeWidth={1.6} className="opacity-80" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="m-2 space-y-0.5 border-t border-line-subtle pt-2">
          <div className="truncate px-2 py-1 text-[12px] text-ink-muted" title={session.user.email}>
            {session.user.email}
            {session.role && <span className="block text-[11px]">{ADMIN_ROLE_LABEL[session.role]}</span>}
          </div>
          <a
            href={session.consoleUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 rounded-md px-2 py-1.5 text-[12.5px] text-ink-muted hover:bg-surface-hover hover:text-ink"
          >
            <ExternalLink className="h-3.5 w-3.5" /> Open the console
          </a>
          <button
            type="button"
            onClick={signOut}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px] text-ink-muted hover:bg-surface-hover hover:text-ink"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>
      <main className="min-w-0 flex-1 overflow-hidden bg-surface shadow-token-sm md:mt-2 md:rounded-tl-2xl md:border-l md:border-t md:border-line">
        <div className="flex gap-1 overflow-x-auto border-b border-line px-3 py-2 md:hidden">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className="whitespace-nowrap rounded px-2 py-1 text-[12px] text-ink-secondary">
              {n.label}
            </NavLink>
          ))}
        </div>
        <Outlet />
      </main>
    </div>
  )
}
